import { queryOptions, useQuery } from '@tanstack/react-query';
import { adminApi } from '@l2u/web-shared';
import { REFERENCE_STALE_TIME } from './adminQueries.ts';

export type TeamRegion = {
  id: string;
  name: string;
  parent_id: string | null;
  display_order: number;
  // Locate2u's short region code, mirrored by the sync (migration 0011). Null
  // for regions created in the portal — read-only here, never sent on write.
  locate2u_region_code: string | null;
  created_at: string;
  updated_at: string;
};

// Managed in Settings → Team Regions. Regions form a tree via parent_id;
// assigning a member a region also grants its entire subtree.
//
// Reference data (REFERENCE_STALE_TIME): read by the shell's region picker and
// every owner form, changed only by Settings → Team Regions and by a save that
// sends a new region NAME — each of which invalidates ['team-regions'].
export function teamRegionsQuery() {
  return queryOptions({
    queryKey: ['team-regions'] as const,
    queryFn: ({ signal }) =>
      adminApi.get<{ team_regions: TeamRegion[] }>('/api/team-regions', { signal }),
    staleTime: REFERENCE_STALE_TIME,
  });
}

export function useTeamRegions(enabled = true) {
  return useQuery({ ...teamRegionsQuery(), enabled });
}

export type RegionTreeRow = { region: TeamRegion; depth: number };

// Depth-first flattening for tree rendering: parents first, siblings ordered
// by display_order (ordering is only ever compared WITHIN a sibling group).
// Orphans (parent id not in the list — e.g. mid-refetch) render as roots so
// no region can silently disappear from a picker. Cycle-safe via `seen`.
export function flattenRegionTree(regions: TeamRegion[]): RegionTreeRow[] {
  const ids = new Set(regions.map((r) => r.id));
  const byParent = new Map<string | null, TeamRegion[]>();
  for (const r of regions) {
    const key = r.parent_id !== null && ids.has(r.parent_id) ? r.parent_id : null;
    const list = byParent.get(key) ?? [];
    list.push(r);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.display_order - b.display_order);
  const out: RegionTreeRow[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const r of byParent.get(parent) ?? []) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      out.push({ region: r, depth });
      walk(r.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

// Narrows a flattened tree to the rows matching what the user has typed in the
// region combobox. Matching is case-insensitive substring (not prefix) — region
// names are short and often two words, so "city" should still find "Quezon
// City".
//
// A match keeps its ancestors even when they don't match themselves: rows carry
// a `depth` used for indentation, so returning "Greenhills" alone would render
// an indented row sitting under nothing. Ancestors kept for context stay
// selectable — they're real regions, and picking one is legitimate.
export function filterRegionRows(rows: RegionTreeRow[], query: string): RegionTreeRow[] {
  const q = query.trim().toLowerCase();
  if (q === '') return rows;
  const byId = new Map(rows.map((row) => [row.region.id, row]));
  const keep = new Set<string>();
  for (const row of rows) {
    if (!row.region.name.toLowerCase().includes(q)) continue;
    keep.add(row.region.id);
    // Walk up to the root. Stopping at an already-kept ancestor is safe (its
    // own ancestors were walked when it was added) and keeps this terminating
    // even if the data ever contained a cycle.
    let parentId = row.region.parent_id;
    while (parentId !== null && !keep.has(parentId)) {
      const parent = byId.get(parentId);
      if (!parent) break;
      keep.add(parentId);
      parentId = parent.region.parent_id;
    }
  }
  return rows.filter((row) => keep.has(row.region.id));
}
